import { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { createInvitation, getInvitations, deleteInvitation, resendInvitation } from '@/services/api';
import { 
  Plus, Mail, Phone, Copy, Trash2, RefreshCw, Clock, CheckCircle, 
  Link as LinkIcon, Send, Users, MailOpen 
} from 'lucide-react';

export const InvitationsPage = () => {
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUsed, setShowUsed] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [createdInvitation, setCreatedInvitation] = useState(null);
  
  const [newInvitation, setNewInvitation] = useState({
    email: '',
    phone: '',
    role: 'user',
    expires_days: 7
  });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    loadInvitations();
  }, [showUsed]);

  const loadInvitations = async () => {
    try {
      const data = await getInvitations(showUsed);
      setInvitations(data);
    } catch (error) {
      toast.error('Failed to load invitations');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!newInvitation.email && !newInvitation.phone) {
      toast.error('Please provide email or phone number');
      return;
    }
    
    setCreating(true);
    try {
      const result = await createInvitation(newInvitation);
      setCreatedInvitation(result);
      // Differentiate between "email delivered" and "code created but email
      // delivery failed (domain not verified, no Resend key, etc.)" so the
      // admin can take the right next step.
      if (result.email && result.email_sent) {
        toast.success(`Invitation sent to ${result.email}`);
      } else if (result.email && !result.email_sent) {
        toast.warning('Invitation created — but email could not be sent. Copy the link below and share it manually.');
      } else {
        toast.success('Invitation created — copy the code or link below.');
      }
      loadInvitations();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this invitation?')) return;
    try {
      await deleteInvitation(id);
      toast.success('Invitation deleted');
      loadInvitations();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleResend = async (id) => {
    try {
      const result = await resendInvitation(id);
      const expiry = new Date(result.new_expires_at).toLocaleDateString();
      if (result.email_sent) {
        toast.success(`Reminder email sent · expires ${expiry}`);
      } else if (result.email_error) {
        toast.warning(`Expiry extended to ${expiry}, but email could not be sent: ${result.email_error}`);
      } else {
        toast.success(`Invitation extended until ${expiry}`);
      }
      loadInvitations();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const getRegistrationUrl = (code) => {
    return `${window.location.origin}/join/${code}`;
  };

  const isExpired = (expiresAt) => {
    return new Date(expiresAt) < new Date();
  };

  const activeCount = invitations.filter(i => !i.used && !isExpired(i.expires_at)).length;
  const usedCount = invitations.filter(i => i.used).length;

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="invitations-page">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Invitations</h1>
            <p className="text-muted-foreground mt-1">
              Generate and manage invitation codes for new users
            </p>
          </div>
          
          <Dialog open={showCreateDialog} onOpenChange={(open) => {
            setShowCreateDialog(open);
            if (!open) {
              setCreatedInvitation(null);
              setNewInvitation({ email: '', phone: '', role: 'user', expires_days: 7 });
            }
          }}>
            <DialogTrigger asChild>
              <Button data-testid="create-invitation-btn">
                <Plus className="h-4 w-4 mr-2" />
                Generate Invitation
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>
                  {createdInvitation ? 'Invitation Created!' : 'Generate Invitation Code'}
                </DialogTitle>
                <DialogDescription>
                  {createdInvitation 
                    ? 'Share this code with the user to register' 
                    : 'Create an invitation for a new user'}
                </DialogDescription>
              </DialogHeader>
              
              {createdInvitation ? (
                <div className="space-y-4">
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-center">
                    <p className="text-sm text-green-700 mb-2">Invitation Code</p>
                    <p className="text-3xl font-mono font-bold tracking-wider text-green-800">
                      {createdInvitation.code}
                    </p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Registration Link</Label>
                    <div className="flex gap-2">
                      <Input 
                        value={getRegistrationUrl(createdInvitation.code)} 
                        readOnly 
                        className="font-mono text-sm"
                      />
                      <Button 
                        variant="outline" 
                        size="icon"
                        onClick={() => copyToClipboard(getRegistrationUrl(createdInvitation.code))}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  
                  <div className="flex gap-2 text-sm text-muted-foreground">
                    <Clock className="h-4 w-4" />
                    Expires: {new Date(createdInvitation.expires_at).toLocaleDateString()}
                  </div>
                  
                  <div className="flex gap-2 pt-4">
                    <Button 
                      variant="outline" 
                      className="flex-1"
                      onClick={() => copyToClipboard(createdInvitation.code)}
                    >
                      <Copy className="h-4 w-4 mr-2" />
                      Copy Code
                    </Button>
                    <Button 
                      className="flex-1"
                      onClick={() => {
                        const subject = encodeURIComponent('Your KAIZEN KANRI Invitation');
                        const body = encodeURIComponent(
                          `You've been invited to join KAIZEN KANRI!\n\n` +
                          `Your invitation code: ${createdInvitation.code}\n\n` +
                          `Register here: ${getRegistrationUrl(createdInvitation.code)}\n\n` +
                          `This invitation expires on ${new Date(createdInvitation.expires_at).toLocaleDateString()}.`
                        );
                        window.open(`mailto:${createdInvitation.email || ''}?subject=${subject}&body=${body}`);
                      }}
                    >
                      <Send className="h-4 w-4 mr-2" />
                      Send Email
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Email (optional)</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        type="email"
                        placeholder="user@company.com"
                        value={newInvitation.email}
                        onChange={(e) => setNewInvitation({...newInvitation, email: e.target.value})}
                        className="pl-10"
                        data-testid="invitation-email"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Phone (optional)</Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        type="tel"
                        placeholder="+31 6 1234 5678"
                        value={newInvitation.phone}
                        onChange={(e) => setNewInvitation({...newInvitation, phone: e.target.value})}
                        className="pl-10"
                        data-testid="invitation-phone"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Role</Label>
                      <Select 
                        value={newInvitation.role} 
                        onValueChange={(v) => setNewInvitation({...newInvitation, role: v})}
                      >
                        <SelectTrigger data-testid="invitation-role">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="user">User</SelectItem>
                          <SelectItem value="admin">Admin</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Expires In</Label>
                      <Select 
                        value={String(newInvitation.expires_days)} 
                        onValueChange={(v) => setNewInvitation({...newInvitation, expires_days: parseInt(v)})}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1">1 day</SelectItem>
                          <SelectItem value="3">3 days</SelectItem>
                          <SelectItem value="7">7 days</SelectItem>
                          <SelectItem value="14">14 days</SelectItem>
                          <SelectItem value="30">30 days</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <Button 
                    onClick={handleCreate} 
                    className="w-full" 
                    disabled={creating}
                    data-testid="submit-invitation"
                  >
                    {creating ? 'Generating...' : 'Generate Invitation Code'}
                  </Button>
                </div>
              )}
            </DialogContent>
          </Dialog>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4">
          <Card>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-10 h-10 rounded-sm bg-blue-50 flex items-center justify-center">
                <MailOpen className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{activeCount}</p>
                <p className="text-sm text-muted-foreground">Active Invitations</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-10 h-10 rounded-sm bg-green-50 flex items-center justify-center">
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{usedCount}</p>
                <p className="text-sm text-muted-foreground">Used</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-10 h-10 rounded-sm bg-purple-50 flex items-center justify-center">
                <Users className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{invitations.length}</p>
                <p className="text-sm text-muted-foreground">Total</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filter */}
        <div className="flex items-center gap-2">
          <Button 
            variant={showUsed ? "outline" : "default"} 
            size="sm"
            onClick={() => setShowUsed(false)}
          >
            Active Only
          </Button>
          <Button 
            variant={showUsed ? "default" : "outline"} 
            size="sm"
            onClick={() => setShowUsed(true)}
          >
            Show All
          </Button>
        </div>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-32">
                <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              </div>
            ) : invitations.length === 0 ? (
              <div className="text-center py-12">
                <MailOpen className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">No invitations yet</p>
                <p className="text-sm text-muted-foreground mt-1">Generate an invitation to add new users</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Code</TableHead>
                    <TableHead>Recipient</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Expires</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invitations.map((inv) => (
                    <TableRow key={inv.invitation_id} data-testid={`invitation-row-${inv.invitation_id}`}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <code className="font-mono font-bold">{inv.code}</code>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-6 w-6"
                            onClick={() => copyToClipboard(inv.code)}
                          >
                            <Copy className="h-3 w-3" />
                          </Button>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          {inv.email && <div className="flex items-center gap-1"><Mail className="h-3 w-3" /> {inv.email}</div>}
                          {inv.phone && <div className="flex items-center gap-1"><Phone className="h-3 w-3" /> {inv.phone}</div>}
                          {!inv.email && !inv.phone && <span className="text-muted-foreground">—</span>}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="capitalize">{inv.role}</Badge>
                      </TableCell>
                      <TableCell>
                        {inv.used ? (
                          <Badge className="bg-green-100 text-green-800">Used</Badge>
                        ) : isExpired(inv.expires_at) ? (
                          <Badge variant="destructive">Expired</Badge>
                        ) : (
                          <Badge variant="secondary">Active</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {new Date(inv.expires_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          {!inv.used && (
                            <>
                              <Button 
                                variant="ghost" 
                                size="icon"
                                onClick={() => copyToClipboard(getRegistrationUrl(inv.code))}
                                title="Copy registration link"
                              >
                                <LinkIcon className="h-4 w-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="icon"
                                onClick={() => handleResend(inv.invitation_id)}
                                title="Extend expiration"
                              >
                                <RefreshCw className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => handleDelete(inv.invitation_id)}
                            title="Delete invitation"
                          >
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default InvitationsPage;
